/**
 * Multiverse weather. Built for the 41.0 redesign, disabled when the
 * C-4 capsule suggested weather reports were being read before
 * publication. The forecasts are computed; the publication is the
 * hard part. This renders nothing unless the weather flag flips,
 * which it has not, because of the capsule, and the ferry.
 */

export interface Forecast {
  universe: string;
  day: string;
  sky: "clear" | "overcast" | "inking" | "bridgewise" | "capsule-heavy" | "print-fog";
  high: number;
  low: number;
  note: string;
}

const SKY_NOTES: Record<Forecast["sky"], string> = {
  clear: "no notes",
  overcast: "the ink dries slower",
  inking: "carry a blotter",
  bridgewise: "wind reads left to right",
  "capsule-heavy": "expect deliveries from later",
  "print-fog": "the fold is soft today",
};

function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function forecast(universe: string, days = 5): Forecast[] {
  const rnd = mulberry(
    Array.from(universe).reduce((h, ch) => Math.imul(h ^ ch.charCodeAt(0), 16777619) >>> 0, 7),
  );
  const out: Forecast[] = [];
  for (let i = 0; i < days; i++) {
    const sky = (["clear", "overcast", "inking", "bridgewise", "capsule-heavy", "print-fog"] as const)[Math.floor(rnd() * 6)];
    const high = 12 + Math.floor(rnd() * 18);
    out.push({
      universe,
      day: DAYS[(new Date().getDay() + i) % 7],
      sky,
      high,
      low: high - (4 + Math.floor(rnd() * 6)),
      note: SKY_NOTES[sky],
    });
  }
  return out;
}

/** The weather flag. See: capsule, C-4; ferry, the. */
export const WEATHER_ENABLED = false;

export function renderable(): boolean {
  return WEATHER_ENABLED;
}

export function almanac(universe: string): string {
  const f = forecast(universe, 7);
  const wettest = f.reduce((a, b) => (b.sky === "print-fog" ? b : a), f[0]);
  return `${universe} almanac: ${wettest.day} is the fog day. The rest is commentary.`;
}
